import React from 'react'

import styled from '@emotion/styled'

import { ArcadeIcon } from '@arcade/web/icons/react'

import { useHelpTextId } from '../../hooks/useHelpTextId'
import spacing from '../../theme/spacing'
import { Flex } from '../layout/Flex'
import { getInputStyles } from './arcade-input-styles'
import ArcadeHelpText, { ArcadeHelpTextComponentProps, combineAriaDescribedBy, getErrorIcon } from './ArcadeHelpText'
import { InputProps } from './Input'

export type ArcadeIconSizeVariants = ArcadeIcon

export type ArcadeInputProps = InputProps &
  ArcadeHelpTextComponentProps & {
    // Optional
    LeadingIcon?: ArcadeIconSizeVariants
    TrailingIcon?: ArcadeIconSizeVariants
    trailingIconClickProps?: {
      onClick: () => void
      ariaLabel: string
    }
    shouldShowErrorIcon?: boolean
  }

/**
 * Styles for input, as well as for leading and trailing icons, if any.
 */
const InputContainer = styled.div<{ hasLeadingIcon: boolean; hasTrailingIcon: boolean; isInvalid: boolean }>`
  ${({ hasLeadingIcon, hasTrailingIcon, isInvalid }) =>
    (hasLeadingIcon || hasTrailingIcon || isInvalid) && `position: relative;`}
`

const BaseInput = styled.input<{
  hasLeadingIcon: boolean
  hasTrailingIcon: boolean
  isInvalid: boolean
  disabled: boolean
}>`
  ${({ theme, hasLeadingIcon, hasTrailingIcon, isInvalid, disabled }) =>
    getInputStyles({ theme, hasLeadingIcon, hasTrailingIcon, isInvalid, disabled })}
`

/**
 * Styles for icons within input.
 */
const LeadingIconContainer = styled.div`
  svg {
    position: absolute;
    transform: translateY(-50%);
    left: ${spacing.m};
    top: 50%;
  }
`

const TrailingIconContainer = styled(Flex)`
  position: absolute;
  transform: translateY(-50%);
  right: ${spacing.m};
  top: 50%;
`

const ClickableTrailingIconContainer = styled.button`
  position: absolute;
  transform: translateY(-50%);
  right: ${spacing.m};
  top: 50%;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: ${({ theme }) => theme.pxToRem(2)} solid ${({ theme }) => theme.arcadeColors.semantic.borderProminent};
  }
`

/**
 * Display leading icon, if provided.
 */
const getLeadingIcon = (LeadingIcon?: ArcadeIconSizeVariants, disabled?: boolean) =>
  LeadingIcon ? (
    <LeadingIconContainer>
      <LeadingIcon size={24} variant={disabled ? 'disabled' : 'subtle'} data-testid="arcade-input-leading-icon" />
    </LeadingIconContainer>
  ) : null

/**
 * Display trailing icon, if provided and input is not invalid. If input is invalid and not disabled, display error icon.
 */
const getTrailingIcon = (
  TrailingIcon?: ArcadeIconSizeVariants,
  showTrailingErrorIcon?: boolean,
  disabled?: boolean,
  trailingIconClickProps?: {
    onClick: () => void
    ariaLabel: string
  }
) => {
  const hasClickProps = !!trailingIconClickProps

  return showTrailingErrorIcon && !disabled ? (
    getErrorIcon({ isInvalid: true, shouldShowErrorIcon: true, disabled: !!disabled })
  ) : TrailingIcon ? (
    hasClickProps ? (
      <ClickableTrailingIconContainer
        onClick={disabled ? undefined : trailingIconClickProps.onClick}
        aria-label={trailingIconClickProps.ariaLabel}
        type="button"
        disabled={disabled}
      >
        <TrailingIcon size={24} variant={disabled ? 'disabled' : 'subtle'} data-testid="arcade-input-trailing-icon" />
      </ClickableTrailingIconContainer>
    ) : (
      <TrailingIconContainer alignItems="center" justifyContent="center">
        <TrailingIcon size={24} variant={disabled ? 'disabled' : 'subtle'} data-testid="arcade-input-trailing-icon" />
      </TrailingIconContainer>
    )
  ) : null
}

const ArcadeInput = React.forwardRef<HTMLInputElement, ArcadeInputProps>((props, ref) => {
  const {
    LeadingIcon,
    TrailingIcon,
    helpText,
    HelpTextIcon,
    isInvalid,
    shouldShowErrorIcon = true,
    errorText,
    disabled,
    disabledText,
    trailingIconClickProps,
    'aria-describedby': ariaDescribedBy,
    ...rest
  } = props
  const helpTextId = useHelpTextId({ helpText, errorText, disabledText })
  const showTrailingErrorIcon = isInvalid && shouldShowErrorIcon

  return (
    <>
      <InputContainer hasLeadingIcon={!!LeadingIcon} hasTrailingIcon={!!TrailingIcon} isInvalid={!!isInvalid}>
        <BaseInput
          aria-invalid={!!isInvalid}
          aria-describedby={combineAriaDescribedBy(ariaDescribedBy, helpTextId)}
          isInvalid={!!isInvalid}
          hasLeadingIcon={!!LeadingIcon}
          hasTrailingIcon={!!TrailingIcon}
          disabled={!!disabled}
          {...rest}
          ref={ref}
        />
        {getLeadingIcon(LeadingIcon, disabled)}
        {getTrailingIcon(TrailingIcon, showTrailingErrorIcon, disabled, trailingIconClickProps)}
      </InputContainer>
      <ArcadeHelpText
        helpTextId={helpTextId}
        helpText={helpText}
        HelpTextIcon={HelpTextIcon}
        isInvalid={isInvalid}
        errorText={errorText}
        disabled={disabled}
        disabledText={disabledText}
      />
    </>
  )
})

export default ArcadeInput
